import type { GeometryElement, Point } from 'jsxgraph';
import { bindGrabHandle } from '../../interaction/grab-handle.bind';
import type { JsxgraphThemeService } from '../../jsxgraph-theme.service';
import type { ElementCreatorContext } from '../core/board.types';
import { parseThemeColor, resolveColor } from '../core/color.utils';
import { deepMerge } from '../core/deepMerge';
import { normalizeCoords } from '../core/element.utils';
import { attachReconfigure, bindLabel, createPointLabelContext } from '../label/label.bind';
import { resolveLabelConfig } from '../label/label.model';
import type { ElementTheme, PointStyle } from '../theme/theme.model';
import { buildSnappingAttributes } from './snapping';
import type { PointArgs, PointResult } from './point.model';

/**
 * Build JSXGraph point attributes from style configuration.
 * Maps our simplified color model to JSXGraph's fillColor/strokeColor.
 */
function buildPointAttributes(
  themeService: JsxgraphThemeService,
  baseStyle: Required<Omit<PointStyle, 'fixed' | 'strokeColor' | 'label' | 'snapping' | 'showGrabHandle'>>,
  styleOverrides?: PointStyle,
): Record<string, unknown> {
  const style = deepMerge(baseStyle, styleOverrides);

  // Our 'color' is the fill color (main visual color)
  const fillColor = resolveColor(themeService, style.color);
  // strokeColor is optional - if not set, use transparent (no stroke)
  const strokeColor = styleOverrides?.strokeColor
    ? resolveColor(themeService, styleOverrides.strokeColor)
    : 'transparent';

  return {
    size: style.size,
    face: style.face,
    strokeColor,
    fillColor,
    fillOpacity: style.fillOpacity,
    strokeWidth: styleOverrides?.strokeColor ? style.strokeWidth ?? 1 : 0,
    fixed: styleOverrides?.fixed ?? false,
    visible: style.visible,
    highlight: false,
    showInfobox: false,
    withLabel: false, // We handle labels separately
  };
}

/**
 * Create a point element with optional label.
 * Label and snapping are now configured via the style property.
 *
 * @param ctx - Element creator context (board, container)
 * @param themeService - Theme service for color resolution
 * @param theme - Element theme with defaults
 * @param args - Point creation arguments
 * @returns Point result with element and optional label
 */
export function createPoint(
  ctx: ElementCreatorContext,
  themeService: JsxgraphThemeService,
  theme: ElementTheme,
  args: PointArgs,
): PointResult {
  const { board } = ctx;
  const { coords, name = '', style } = args;

  // Build point attributes (visual)
  const visualAttrs = buildPointAttributes(themeService, theme.point, style);

  // Build snapping attributes (behavioral) - now from style.snapping
  const snappingAttrs = buildSnappingAttributes(ctx, style?.snapping);

  // Merge and create the point
  const normalizedCoords = normalizeCoords(coords);
  const point = board.create('point', normalizedCoords, {
    ...visualAttrs,
    ...snappingAttrs,
  }) as GeometryElement;

  const result: PointResult = { point } as PointResult;

  // Always create label — visibility is controlled via config, never by skipping creation
  const labelConfig = resolveLabelConfig(style?.label, {
    color: parseThemeColor(style?.color ?? 'neutral').name,
    precision: ctx.defaultPrecision,
  });

  if (style?.visible === false) {
    labelConfig.visible = false;
  }
  result.label = bindLabel({
    ctx,
    themeService,
    element: point,
    config: labelConfig,
    contextProvider: createPointLabelContext(name || '', point),
  });
  attachReconfigure(point, result.label);

  // Create grab handle if configured
  if (style?.showGrabHandle) {
    const fillColor = resolveColor(themeService, style?.color ?? theme.point.color) ?? 'currentColor';
    result.grabHandle = bindGrabHandle({
      ctx,
      point: point as unknown as Point,
      color: fillColor,
      snapping: style?.snapping,
      onDrag: args.grabHandle?.onDrag,
    });
  }

  return result;
}
